import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Category } from "../types/restaurant";
import { usePaymentTheme } from "../../respayment/hooks/usePaymentTheme";

interface CategoryCarouselProps {
  categories: Category[];
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

const CategoryCarousel: React.FC<CategoryCarouselProps> = ({
  categories,
  selectedCategory,
  setSelectedCategory,
  currentPage,
  setCurrentPage,
}) => {
  const { getBgClass, fontClass } = usePaymentTheme();
  const categoriesPerPage = 7;
  const totalPages = Math.ceil((categories?.length || 0) / categoriesPerPage);

  return (
    <div className="-mt-1">
      <div className="relative">
        {/* Left Button */}
        <button
          className={`absolute left-0.5 top-1/2 transform -translate-y-1/2 z-10 ${getBgClass()} backdrop-blur-xl shadow-lg rounded-full p-2 transition-all border border-white/20 ${
            currentPage === 0 ? "opacity-50 cursor-not-allowed" : "text-white hover:shadow-xl"
          }`}
          onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
          disabled={currentPage === 0}
        >
          <ChevronLeft className="w-[20px] h-[20px]" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} />
        </button>

        <div className={`grid grid-cols-4 gap-2 mx-9 ${fontClass}`}>
          {/* ALL Button - Always visible */}
          <button
            className={`px-2 py-2 rounded-3xl text-sm font-bold transition-all truncate whitespace-nowrap text-ellipsis ${
              selectedCategory === null ? `${getBgClass()} text-white shadow-lg` : "text-gray-700 bg-gray-300/50 backdrop-blur-sm"
            }`}
            onClick={() => setSelectedCategory(null)}
          >
            ALL
          </button>

          {/* Categories */}
          {categories
            ?.slice(currentPage * categoriesPerPage, (currentPage + 1) * categoriesPerPage)
            .map((category) => (
              <button
                key={category._id.$oid}
                className={`px-2 py-2 rounded-3xl text-sm font-bold transition-all truncate whitespace-nowrap text-ellipsis ${
                  selectedCategory === category._id.$oid ? `${getBgClass()} text-white shadow-lg` : "text-gray-700 bg-gray-300/50 backdrop-blur-sm"
                }`}
                onClick={() => setSelectedCategory(category._id.$oid)}
              >
                {category.CategoryName}
              </button>
            ))
            .concat(
              Array.from({ length: categoriesPerPage - Math.min(categories?.slice(currentPage * categoriesPerPage, (currentPage + 1) * categoriesPerPage).length || 0, categoriesPerPage) }, (_, index) => (
                <div key={`empty-${index}`} className="px-2 py-2 rounded-3xl text-sm font-bold text-transparent bg-transparent">
                  Empty
                </div>
              ))
            )}
        </div>

        {/* Right Button */}
        <button
          className={`absolute right-0.5 top-1/2 transform -translate-y-1/2 z-10 ${getBgClass()} backdrop-blur-xl shadow-lg rounded-full p-2 transition-all border border-white/20 ${
            currentPage >= totalPages - 1 ? "opacity-50 cursor-not-allowed" : "text-white hover:shadow-xl"
          }`}
          onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
          disabled={currentPage >= totalPages - 1}
        >
          <ChevronRight className="w-[20px] h-[20px]" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} />
        </button>
      </div>
    </div>
  );
};

export default CategoryCarousel;